import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AlertCircle, Eye, EyeOff } from "lucide-react";
import {
  useAnimationConfig,
  useReducedMotion,
} from "../lib/animations/context";

interface EnhancedFormFieldProps {
  label: string;
  type?: "text" | "email" | "tel" | "password" | "textarea";
  value: string | undefined;
  onChange: (value: string) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  error?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  rows?: number;
  maxLength?: number;
  showCharacterCount?: boolean;
  validateOnType?: boolean;
  validator?: (value: string) => string | null;
  id?: string;
}

const EnhancedFormField: React.FC<EnhancedFormFieldProps> = ({
  label,
  type = "text",
  value,
  onChange,
  onBlur,
  onFocus,
  error,
  placeholder,
  required = false,
  disabled = false,
  className = "",
  rows = 4,
  maxLength,
  showCharacterCount = false,
  validateOnType = false,
  validator,
  id,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const config = useAnimationConfig();
  const isReducedMotion = useReducedMotion();

  // Simple validation
  useEffect(() => {
    if (validateOnType && validator && value) {
      const validationResult = validator(value);
      setValidationError(validationResult);
    } else {
      setValidationError(null);
    }
  }, [value, validator, validateOnType]);

  const handleFocus = () => {
    setIsFocused(true);
    onFocus?.();
  };

  const handleBlur = () => {
    setIsFocused(false);
    onBlur?.();
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newValue = e.target.value;
    if (maxLength && newValue.length > maxLength) return;
    onChange(newValue);
  };

  const displayError = error || validationError;
  const hasValue = Boolean(value && value.length > 0);
  const characterCount = value ? value.length : 0;
  const isOverLimit = maxLength ? characterCount > maxLength * 0.9 : false;

  // Animation variants
  const labelVariants = {
    default: {
      scale: 1,
      y: 0,
      color: "#6B7280",
      transition: { duration: config.customTimings.fast },
    },
    focused: {
      scale: 0.85,
      y: -32,
      color: "#FF5A1F",
      transition: { duration: config.customTimings.fast },
    },
    filled: {
      scale: 0.85,
      y: -32,
      color: "#6B7280",
      transition: { duration: config.customTimings.fast },
    },
  };

  const fieldVariants = {
    default: {
      borderColor: "#D1D5DB",
      boxShadow: "0 0 0 0px rgba(255, 90, 31, 0)",
      scale: 1,
      transition: { duration: config.customTimings.fast },
    },
    focused: {
      borderColor: "#FF5A1F",
      boxShadow: "0 0 0 3px rgba(255, 90, 31, 0.1)",
      scale: isReducedMotion ? 1 : 1.01,
      transition: { duration: config.customTimings.fast },
    },
    error: {
      borderColor: "#EF4444",
      boxShadow: "0 0 0 3px rgba(239, 68, 68, 0.1)",
      scale: isReducedMotion ? 1 : 0.99,
      transition: { duration: config.customTimings.fast },
    },
  };

  const errorVariants = {
    hidden: {
      opacity: 0,
      y: -10,
      scale: 0.95,
      transition: { duration: config.customTimings.fast },
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: config.customTimings.fast,
        type: "spring" as const,
        stiffness: 300,
        damping: 25,
      },
    },
  };

  // Determine field state for styling
  const getFieldState = () => {
    if (displayError) return "error";
    if (isFocused) return "focused";
    return "default";
  };

  // Determine label state
  const getLabelState = () => {
    if (isFocused) return "focused";
    if (hasValue) return "filled";
    return "default";
  };

  const fieldState = getFieldState();
  const labelState = getLabelState();

  const commonProps = {
    id: id || label.toLowerCase().replace(/\s+/g, "-"),
    value,
    onChange: handleChange,
    onFocus: handleFocus,
    onBlur: handleBlur,
    placeholder: isFocused ? placeholder : "",
    disabled,
    className: `w-full px-4 py-3 border-2 rounded-lg bg-white transition-all duration-200 focus:outline-none ${
      disabled ? "bg-gray-50 cursor-not-allowed" : ""
    }`,
    style: {
      borderColor: fieldVariants[fieldState].borderColor,
      boxShadow: fieldVariants[fieldState].boxShadow,
    },
  };

  return (
    <div className={`relative ${className}`}>
      {/* Field Container */}
      <motion.div
        className="relative"
        animate={fieldState}
        variants={fieldVariants}
        initial="default"
      >
        {/* Input/Textarea */}
        {type === "textarea" ? (
          <textarea
            {...commonProps}
            ref={textareaRef}
            rows={rows}
            style={{
              ...commonProps.style,
              resize: "none",
              minHeight: `${rows * 1.5}rem`,
            }}
          />
        ) : (
          <input
            {...commonProps}
            ref={inputRef}
            type={
              type === "password" ? (showPassword ? "text" : "password") : type
            }
          />
        )}

        {/* Floating Label */}
        <motion.label
          htmlFor={commonProps.id}
          className="absolute left-4 pointer-events-none select-none font-medium origin-left px-1"
          animate={labelState}
          variants={labelVariants}
          initial="default"
          style={{
            top: type === "textarea" ? "1rem" : "0.75rem",
            backgroundColor: isFocused || hasValue ? "white" : "transparent",
            zIndex: 10,
          }}
        >
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </motion.label>

        {/* Password Toggle */}
        {type === "password" && (
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
          >
            {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
          </button>
        )}
      </motion.div>

      {/* Character Count */}
      {showCharacterCount && maxLength && (
        <div className="flex justify-end mt-1">
          <motion.span
            className={`text-xs transition-colors duration-200 ${
              isOverLimit ? "text-orange-500" : "text-gray-500"
            }`}
            animate={
              isOverLimit && !isReducedMotion ? { scale: [1, 1.05, 1] } : {}
            }
            transition={{ duration: 0.2 }}
          >
            {characterCount}/{maxLength}
          </motion.span>
        </div>
      )}

      {/* Error Message */}
      <AnimatePresence>
        {displayError && (
          <motion.div
            className="flex items-center space-x-2 mt-2"
            variants={errorVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
          >
            <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
            <span className="text-sm text-red-600">{displayError}</span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default EnhancedFormField;
